import React from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { UserSummary } from '../../../api/duplicates';

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function tgHandle(u: Pick<UserSummary, 'telegramUsername' | 'telegramId'>): string {
  if (u.telegramUsername) return `@${u.telegramUsername}`;
  if (u.telegramId) return `id ${u.telegramId}`;
  return '—';
}

export function userTitle(u: UserSummary): string {
  return u.username || u.email || u.firstName || u.id.slice(0, 8);
}

/** Премиум активен, если подписка отмечена и срок ещё не истёк (или срок не указан). */
function premiumActive(u: UserSummary): boolean {
  if (!u.isSubscribed) return false;
  if (!u.subscribedEnd) return true;
  return new Date(u.subscribedEnd).getTime() > Date.now();
}

/** Достаём человекочитаемую ошибку из ответа Nest (message бывает строкой, массивом или объектом). */
export function apiErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (getErrorCode(err) === 'DUPLICATE_HAS_PAYMENTS') {
    return 'У дубля есть оплаты или подписки — склейка запрещена. Решите вручную: возврат или продление на основном аккаунте.';
  }
  const msg = data?.message;
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) return msg.join(', ');
  if (msg && typeof msg.message === 'string') return msg.message;
  return fallback;
}

export function getErrorCode(err: any): string | undefined {
  const data = err?.response?.data;
  return data?.code ?? data?.message?.code;
}

function Field({ label, children, isDark }: { label: string; children: React.ReactNode; isDark: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm py-0.5">
      <span className={cn('shrink-0', isDark ? 'text-slate-400' : 'text-slate-500')}>{label}</span>
      <span
        className={cn(
          'text-right break-all font-medium',
          isDark ? 'text-slate-100' : 'text-[#111111]',
        )}
      >
        {children}
      </span>
    </div>
  );
}

interface UserSummaryCardProps {
  user: UserSummary;
  role: string;
  isDark: boolean;
  /** Подсветка роли: дубль (Telegram) — голубой, основной — золотой */
  tone?: 'duplicate' | 'main' | 'neutral';
  footer?: React.ReactNode;
}

export function UserSummaryCard({ user, role, isDark, tone = 'neutral', footer }: UserSummaryCardProps) {
  const premium = premiumActive(user);
  return (
    <div
      className={cn(
        'rounded-xl border-2 p-4 flex flex-col gap-2 min-w-0',
        tone === 'duplicate'
          ? 'border-sky-500/40 bg-sky-500/5'
          : tone === 'main'
          ? 'border-[#C9A227]/50 bg-[#C9A227]/5'
          : isDark
          ? 'border-slate-700'
          : 'border-slate-200',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className={cn(
              'text-[11px] uppercase tracking-wide font-semibold',
              tone === 'duplicate'
                ? 'text-sky-600'
                : tone === 'main'
                ? 'text-[#9C7E1B]'
                : isDark
                ? 'text-slate-400'
                : 'text-slate-500',
            )}
          >
            {role}
          </p>
          <p className={cn('font-semibold truncate', isDark ? 'text-white' : 'text-[#111111]')}>
            {userTitle(user)}
          </p>
          <p className={cn('text-[11px] font-mono break-all', isDark ? 'text-slate-500' : 'text-slate-400')}>
            {user.id}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {premium && (
            <Badge className="rounded-lg border-0 bg-[#C9A227]/15 text-[#9C7E1B]">Премиум</Badge>
          )}
          {user.hasPayments && (
            <Badge className="rounded-lg border-0 bg-emerald-500/10 text-emerald-600">Есть оплаты</Badge>
          )}
          {user.mergedIntoUserId && (
            <Badge className="rounded-lg border-0 bg-slate-500/15 text-slate-500">Склеен</Badge>
          )}
          {user.isDeleted && (
            <Badge className="rounded-lg border-0 bg-red-500/10 text-red-500">Удалён</Badge>
          )}
        </div>
      </div>

      <div className={cn('border-t pt-2', isDark ? 'border-slate-700' : 'border-slate-100')}>
        <Field label="Username" isDark={isDark}>{user.username || '—'}</Field>
        <Field label="Email" isDark={isDark}>{user.email || '—'}</Field>
        <Field label="Телефон" isDark={isDark}>{user.phone || '—'}</Field>
        <Field label="Telegram" isDark={isDark}>{tgHandle(user)}</Field>
        <Field label="Создан" isDark={isDark}>{formatDate(user.createdAt)}</Field>
        <Field label="Последний вход" isDark={isDark}>{formatDate(user.lastLogin)}</Field>
        <Field label="Премиум до" isDark={isDark}>
          {user.isSubscribed ? formatDate(user.subscribedEnd) : '—'}
        </Field>
        <Field label="Оплаты" isDark={isDark}>{user.hasPayments ? 'есть' : 'нет'}</Field>
      </div>

      {footer && <div className="pt-1">{footer}</div>}
    </div>
  );
}
